/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, FormGroup, Form } from 'react-bootstrap'
import { Formik, type FormikProps } from 'formik'
import cloneDeep from 'lodash/cloneDeep'
import Modal from '~/components/Modal'
import { useServiceContext } from '~/root'
import Field from '~/components/shared/Field'
import { ErrorMessageStyled } from '~/components/ErrorMessage'
import { File } from '~/constants/file'
import { useEsignature, type ValidationResult } from '~/hooks/useEsignature'
import { ApiError } from '~/services/api/base'

interface FileDetailsForRename {
  id: number
  name: string
  type?: string
  actionFlags?: {
    eosSiteAndCanBeRenamed?: boolean
  }
}

interface RenameModalProps {
  fileDetails: FileDetailsForRename
  onClose?: () => void
}

interface RenameFormValues {
  nameExlExt: string
  nameExt?: string
}

const RenameFolderAndDocumentModal: React.FC<RenameModalProps> = ({ fileDetails, onClose }) => {
  const context = useServiceContext()
  const { toast, auth } = context.service
  const { api, $rootScope } = context
  const { translate } = context.service.filter
  const [isOpen, setIsOpen] = useState(false)
  const [fileId, setFileId] = useState<number | null>(null)
  const { signAction } = useEsignature()
  const [initialValues, setInitialValues] = useState<RenameFormValues>({
    nameExlExt: '',
    nameExt: undefined
  })

  useEffect(() => {
    setIsOpen(true)
    getName()
  }, [fileDetails])

  const doClose = (): void => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const getName = (): void => {
    if (fileDetails?.id) {
      setFileId(cloneDeep(fileDetails.id))
      const values: RenameFormValues = { nameExlExt: '', nameExt: undefined }

      if (fileDetails.type && [File.type.DOCUMENT, File.type.REFERENCE].includes(fileDetails.type)) {
        if (fileDetails?.name?.indexOf('.') !== -1) {
          values.nameExt = '.' + fileDetails.name.split('.').pop()
          values.nameExlExt = fileDetails.name.split('.').slice(0, -1).join('.')
        } else {
          values.nameExlExt = fileDetails.name
        }
      } else {
        values.nameExlExt = fileDetails.name
      }

      setInitialValues(values)
    } else {
      doClose()
    }
  }

  const save = (values: RenameFormValues): void => {
    const doRenameCallback = async (validation?: ValidationResult): Promise<void> => {
      try {
        const apiCall = fileDetails.actionFlags?.eosSiteAndCanBeRenamed
          ? api.eos.updateSiteFolderName(fileId!, values.nameExlExt)
          : api.files.renameFile(fileId!, {
              name: values.nameExlExt + (values.nameExt || ''),
              esignature: validation as Record<string, unknown>
            })

        await apiCall

        toast.success(
          fileDetails.type !== File.type.FOLDER
            ? translate('BROWSER_RENAME_SUCCESS_MSG')
            : translate('BROWSER_RENAME_FOLDER_SUCCESS_MSG')
        )

        window.dispatchEvent(new CustomEvent('updateFiles', { detail: 'RENAME_FSI' }))
        window.dispatchEvent(new CustomEvent('updateDashboard', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateSearchFiles'))
        window.dispatchEvent(new CustomEvent('updateFavorites'))
        window.dispatchEvent(new CustomEvent('updateReports'))
        window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))

        const browser = auth.getBrowser(File.systemFolderType.REGULATED_CONTENT)
        if (browser && 'id' in browser && fileId === browser.id) {
          auth.refreshUserPolicies()
        }
      } catch (error: unknown) {
        if (error instanceof ApiError) {
          const body = error.body as { code?: number; message?: string } | undefined
          if (body?.code === 417) {
            toast.error(translate('UNDERLYINF_DATA_WERE_CHNGD_TRY_AGAIN'))
          } else {
            toast.error(translate('ERROR_ERROR', { error: body?.message }))
          }
        }
      }
    }

    if (auth.requireESignatureForFileCommands()) {
      signAction('ADMIN_ACTION', 'ADMIN_ACTION_RENAME').then((validation) => {
        doRenameCallback(validation)
      })
    } else {
      doRenameCallback()
    }
  }

  const validateForm = (values: RenameFormValues): { nameExlExt?: string } => {
    const errors: { nameExlExt?: string } = {}
    if (!values?.nameExlExt || values.nameExlExt === '') {
      errors.nameExlExt = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
    }
    return errors
  }

  return (
    <Formik
      validate={validateForm}
      enableReinitialize={true}
      innerRef={(el: FormikProps<RenameFormValues> | null) => {
        if (context.store && el) {
          context.store.formRef = el
        }
      }}
      initialValues={initialValues}
      onSubmit={(values) => {
        save(values)
        doClose()
      }}
    >
      {({ values, handleSubmit }) => (
        <Modal
          show={isOpen}
          handleClose={doClose}
          title={
            fileDetails?.actionFlags?.eosSiteAndCanBeRenamed
              ? translate('BROWSER_ACTION_RENAME_SITE')
              : translate('BROWSER_RENAME_ITEM')
          }
        >
          <Modal.Body>
            <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
              {translate('BROWSER_RENAME_EXISTING_NAME')}
            </Form.Label>
            <p className="whitespaceprewrap" style={{ fontSize: '12px' }}>
              {fileDetails.name}
            </p>
            <div className="form-group md-form-group">
              <FormGroup
                controlId="6cea54c0-817c-4054-9cab-090e2aaa3f4f"
                className={'mb0 w100p pos-relative' + (initialValues.nameExt ? ' w88p' : '')}
              >
                <Form.Label style={{ fontSize: '12px', marginBottom: 0 }}>
                  {translate('BROWSER_RENAME_NEW_NAME')}{' '}
                </Form.Label>
                <Field
                  type="text"
                  style={{ fontSize: '12px' }}
                  name="nameExlExt"
                  maxLength={234}
                  id="6cea54c0-817c-4054-9cab-090e2aaa3f4f"
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleSubmit()
                    }
                  }}
                />
                <ErrorMessageStyled name="nameExlExt" classesName="fs14" />
                {initialValues.nameExt && <span className="ext-txt">{initialValues.nameExt}</span>}
              </FormGroup>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button data-action={'ok'} variant="link" onClick={doClose} id="2d4581db-1001-40d0-9746-d78086da4581">
              {translate('CANCEL')}
            </Button>
            <Button
              data-action={'ok'}
              className="btn btn-primary"
              onClick={() => handleSubmit()}
              id="007b9374-ceb3-42e5-9e43-a20f20522f87"
            >
              {translate('OK')}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Formik>
  )
}

export default RenameFolderAndDocumentModal
